import React, { useEffect, useState } from "react";
import ChartComponent from "../webappStats/ChartComponent";
import HelperMetiers360 from "../../services/HelpersMetiers360";
import axios from "../../services/axios";
import Loader from "../widgets/Loader";
import { useStoreState } from "easy-peasy";
import { Button, Card, Col, Row, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import useCustomGetStoreState from "../../hooks/useCustomGetStoreState";
import SelectMultipleClientsComponent from "../widgets/selectClient/SelectMultipleClientsComponent";

const HeadsetEventsStatComponent = () => {
  const {
    apiData: { ACTIONS },
  } = useStoreState((state) => state.actionSlugs);

    const { allClients } = useCustomGetStoreState('clients');
    const [isFetching, setIsFetching] = useState(false);
    const [isChronogramFetching, setIsChronogramFetching] = useState(false);
  const [graphsData, setGraphsData] = useState({});
  const [isAvailableData, setIsAvailableData] = useState({});
  const [clientTypeSelected, setClientTypeSelected] = useState("all");
    const [isUniqueGroupEventToDisplay, setIsUniqueGroupEventToDisplay] = useState(false);
    const [selectedClientUniqueIds, setSelectedClientUniqueIds] = useState([]);
    const [isExpand, setIsExpand] = useState(false);
    const [aggregate, setAggregate] = useState(false);

  const fetchDataForGraph = async () => {
    if (!ACTIONS?.headsetEventsStats || isFetching) return;

    setIsFetching(true);
    const entries = Object.entries(ACTIONS.headsetEventsStats);

    try {
      const results = await Promise.allSettled(
        entries.map(async ([key, stat]) => {
          if (!stat.url) return [key, null];
          const response = await axios.post(
            stat.url,
            {
                clientType: clientTypeSelected,
                clientUniqueIds: selectedClientUniqueIds,
                aggregate
            },
            { responseType: "json" },
          );
          return [key, response];
        }),
      );

      const dataObject = Object.fromEntries(
        results.filter((r) => r.status === "fulfilled").map((r) => r.value),
      );
      const graphDataObject = Object.fromEntries(
        Object.entries(dataObject).map(([name, value]) => [
          name,
          value?.length ? value : [],
        ]),
      );
      const isAvailableObject = Object.fromEntries(
        Object.entries(dataObject).map(([name, value]) => [
          name,
          !!value?.length,
        ]),
      );

      setGraphsData(graphDataObject);
      setIsAvailableData(isAvailableObject);
    } catch (error) {
      Promise.reject(new Error(error));
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchDataForGraph();
  }, []);

    const fetchChronogramGroupsCountGraph = async () => {
        if (!ACTIONS?.headsetEventsStats?.chronogramGroupsCount?.url || isChronogramFetching) return;

        setIsChronogramFetching(true);

        try {
            const response = await axios.post(
                ACTIONS?.headsetEventsStats?.chronogramGroupsCount?.url,
                {
                    clientType: clientTypeSelected,
                    clientUniqueIds: selectedClientUniqueIds,
                    aggregate
                },
                { responseType: "json" },
            );

            if (response?.length > 0) {
                setGraphsData(prev => ({ ...prev, chronogramGroupsCount: response }));
            }
        } catch (error) {
            Promise.reject(new Error(error));
        } finally {
            setIsChronogramFetching(false);
        }
    };

    useEffect(() => {
        fetchChronogramGroupsCountGraph();
    }, [aggregate]);

    const tableCsv = {
        visitedGroups: graphsData?.visitedGroups?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.count, b.count)
        ),
        groupsCountBySession: graphsData?.groupsCountBySession?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.group_events_count, b.group_events_count, true)
        ),
        uniqueGroupsCountBySession: graphsData?.uniqueGroupsCountBySession?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.group_events_count, b.group_events_count, true)
        ),
        videoPlayCountBySession: graphsData?.videoPlayCountBySession?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.video_play_events_count, b.video_play_events_count, true)
        ),
        chronogramGroupsCount: graphsData?.chronogramGroupsCount,
    };

    const csvSort = {
        visitedGroups: [
            {
                value: "group_name",
                label: "Groupe",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.visitedGroups, "group_name"),
                display: (data) => data.group_name,
            },
            {
                value: "count",
                label: "Nombre d'évènements",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.visitedGroups, "count"),
                display: (data) => data.count,
            },
        ],
        groupsCountBySession: [
            {
                value: "group_events_count",
                label: "Nombre de groupes visités",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.groupsCountBySession, "group_events_count"),
                display: (data) => data.group_events_count,
            },
            {
                value: "sessions_count",
                label: "Nombre de sessions",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.groupsCountBySession, "sessions_count"),
                display: (data) => data.sessions_count,
            },
        ],
        uniqueGroupsCountBySession: [
            {
                value: "group_events_count",
                label: "Nombre de groupes visités",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.uniqueGroupsCountBySession, "group_events_count"),
                display: (data) => data.group_events_count,
            },
            {
                value: "sessions_count",
                label: "Nombre de sessions",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.uniqueGroupsCountBySession, "sessions_count"),
                display: (data) => data.sessions_count,
            },
        ],
        videoPlayCountBySession: [
            {
                value: "video_play_events_count",
                label: "Nombre de lecture d'expériences immersives",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.videoPlayCountBySession, "video_play_events_count"),
                display: (data) => data.video_play_events_count,
            },
            {
                value: "sessions_count",
                label: "Nombre de sessions",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.videoPlayCountBySession, "sessions_count"),
                display: (data) => data.sessions_count,
            },
        ],
        chronogramGroupsCount: [
            {
                value: "group_name",
                label: "Groupe",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.chronogramGroupsCount, "group_name"),
                display: (data) => data.group_name,
            },
            {
                value: "date_week",
                label: "Semaine",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.chronogramGroupsCount, "date_week"),
                display: (data) => data.date_week,
            },
            {
                value: "date_year",
                label: "Année",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.chronogramGroupsCount, "date_year"),
                display: (data) => data.date_year,
            },
            {
                value: "count_group_events_week",
                label: "Nombre d'évènements",
                test: HelperMetiers360.isArrayContainsValue(graphsData?.chronogramGroupsCount, "count_group_events_week"),
                display: (data) => data.count_group_events_week,
            },
        ],
  };

  const clientTypes = [
    { value: "all", label: "Tous" },
    { value: "withoutM360AndArmy", label: "Sans M360 et l'armée" },
    { value: "onlyArmy", label: "Uniquement l'armée" },
    { value: "onlyM360", label: "Uniquement M360" },
  ];
  const clientTypeFilter = (
    <div className="d-flex align-items-center">
      <label className="label-select">Établissements&nbsp;: </label>
      <div className="choices-selector-inline">
        {clientTypes.map((clientType) => (
          <div
                key={clientType.value}
            className={
              clientTypeSelected === clientType.value ? "selected" : null
            }
            onClick={() => setClientTypeSelected(clientType.value)}
            onKeyDown={() => setClientTypeSelected(clientType.value)}
          >
            {clientType.label}
          </div>
        ))}
      </div>
    </div>
  );

    const clientsFilter = allClients?.length > 1 && <>
        <Row className="stats-select-clients">
            <Card className="contents_details_card">
                <Card.Title className="contents_details_title d-flex justify-content-between mx-4 cursor-pointer"
                    onClick={() => setIsExpand(prev => !prev)}>
                    {selectedClientUniqueIds?.length +
                        (selectedClientUniqueIds?.length > 1
                            ? " établissements sélectionnés"
                            : " établissement sélectionné")
                        + " pour analyse"}
                </Card.Title>
                <Card.Body className={"contents_details_body" + (isExpand ? "" : " visually-hidden")}>
                    <SelectMultipleClientsComponent
                        disabled={isFetching}
                        nameSelect={`stats[clients][]`}
                        selectedClients={selectedClientUniqueIds}
                        setSelectedClients={setSelectedClientUniqueIds} />
                </Card.Body>
            </Card>
        </Row>
        <div className="expand-btn cursor-pointer" onClick={() => setIsExpand(prev => !prev)}>
            {isExpand ? <i className="fa fa-angle-up fa-lg" /> : <i className="fa fa-angle-down fa-lg" />}
        </div>
    </>;

    return <>
        <div className="mb-5">
            <Row>
                {clientsFilter}
            </Row>
            <Row>{clientTypeFilter}</Row>
            <Row className="justify-content-center">
                <Button
                    id="btnInitFilters"
                    className="shadow w-auto"
                    variant="success"
                    size="sm"
                    onClick={fetchDataForGraph}
                >
                    Filtrer
                </Button>
            </Row>
        </div>
        {isFetching
            ? <Loader />
            : Object.keys(graphsData).length !== 0 && <>
                <Row>
                    <Col>
                        <ChartComponent
                            title="Top 5 du nombre d'entrées dans les groupes"
                            tableCsv={tableCsv?.visitedGroups}
                            csvSort={csvSort?.visitedGroups}
                            indexTable="group_name"
                            csvFilename="nb_entrees_dans_groupes"
                            graphType="barTop5"
                            data={graphsData?.visitedGroups?.slice(0, 5)}
                            isDataAvailable={isAvailableData?.visitedGroups}
                            dataNamesLabel="group_name"
                            dataValuesLabel="count"
                        />
                    </Col>
                    <Col>
                        <ChartComponent
                            title={isUniqueGroupEventToDisplay
                                ? "Nombre de sessions par nombre d'entrées dans un groupe unique"
                                : "Nombre de sessions par nombre d'entrées dans un groupe"}
                            tableCsv={isUniqueGroupEventToDisplay
                                ? tableCsv?.uniqueGroupsCountBySession
                                : tableCsv?.groupsCountBySession}
                            csvSort={isUniqueGroupEventToDisplay
                                ? csvSort?.uniqueGroupsCountBySession
                                : csvSort?.groupsCountBySession}
                            csvFilename={isUniqueGroupEventToDisplay
                                ? "nb_sessions_par_nombre_entrees_dans_un_groupe_unique"
                                : "nb_sessions_par_nombre_entrees_dans_un_groupe"}
                            graphType="bar"
                            data={isUniqueGroupEventToDisplay
                                ? graphsData?.uniqueGroupsCountBySession
                                : graphsData?.groupsCountBySession}
                            isDataAvailable={isUniqueGroupEventToDisplay
                                ? isAvailableData?.uniqueGroupsCountBySession
                                : isAvailableData?.groupsCountBySession}
                            dataNamesLabel="group_events_count"
                            dataValuesLabel="sessions_count"
                            toggleButton={<ToggleButtonGroup type="radio" name="uniqueGroupEvent" onChange={(type) => setIsUniqueGroupEventToDisplay(type)}
                                value={isUniqueGroupEventToDisplay} className='ms-2'>
                                <ToggleButton value={false} key="all" id="all">Tous</ToggleButton>
                                <ToggleButton value={true} key="uniqueGroups" id="uniqueGroups">Groupes uniques</ToggleButton>
                            </ToggleButtonGroup>}
                        />
                    </Col>
                </Row>
                <Row>
                    <ChartComponent
                        title="Nombre de sessions par nombre de visionnage d'expériences immersives"
                        tableCsv={tableCsv?.videoPlayCountBySession}
                        csvSort={csvSort?.videoPlayCountBySession}
                        csvFilename="nb_sessions_par_nombre_visionnage_experiences_immersives"
                        graphType="bar"
                        data={graphsData?.videoPlayCountBySession}
                        isDataAvailable={isAvailableData?.videoPlayCountBySession}
                        dataNamesLabel="video_play_events_count"
                        dataValuesLabel="sessions_count"
                    />
                </Row>
                <Row>
                    <ChartComponent
                        title="Évolution dans le temps du nombre d'entrées dans les groupes hebdomadaire"
                        tableCsv={tableCsv?.chronogramGroupsCount}
                        csvSort={csvSort?.chronogramGroupsCount}
                        csvFilename={"evolution_nombre_entrees_groupes_hebdomadaire" + (aggregate ? "_agrege" : "")}
                        graphType="chronogram"
                        data={graphsData?.chronogramGroupsCount}
                        isDataAvailable={isAvailableData?.chronogramGroupsCount}
                        itemLabel="group"
                        dataValuesLabel="count_group_events_week"
                        isFetching={isChronogramFetching}
                        aggregatable={true}
                        aggregate={aggregate}
                        setAggregate={setAggregate}
                    />
                </Row>
            </>
        }
    </>
};

export default HeadsetEventsStatComponent;
